import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classes from './Kontakt.module.css';
import { BottomFooter } from '../../components/BottomFooter';
import {
  CONTACT_FIELD_LIMITS,
  useSubmitContactMessage,
} from '../../hooks/useSubmitContactMessage';
import { trackLead } from '../../util/tracking';

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="2" y="3.33" width="12" height="9.33" rx="1.33" stroke="#DF315A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 4.67L8 9 14 4.67" stroke="#DF315A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M14.67 11.28v2a1.33 1.33 0 0 1-1.45 1.33 13.2 13.2 0 0 1-5.76-2.05 13 13 0 0 1-4-4A13.2 13.2 0 0 1 1.4 2.78 1.33 1.33 0 0 1 2.73 1.33h2a1.33 1.33 0 0 1 1.33 1.15c.08.64.23 1.27.44 1.87a1.33 1.33 0 0 1-.3 1.41L5.36 6.6a10.67 10.67 0 0 0 4 4l.84-.84a1.33 1.33 0 0 1 1.41-.3c.6.21 1.23.36 1.87.44a1.33 1.33 0 0 1 1.15 1.38z"
      stroke="#DF315A"
      strokeWidth="1.33"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoCard = ({ icon, label, value, href }) => (
  <a href={href} className={classes.infoCard}>
    <span className={classes.infoCardIcon}>{icon}</span>
    <span className={classes.infoCardText}>
      <span className={classes.infoCardLabel}>{label}</span>
      <span className={classes.infoCardValue}>{value}</span>
    </span>
  </a>
);

const StudioSwitch = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-label="Interesse an Studio-Listing"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`${classes.switch} ${checked ? classes.switchOn : ''}`}
  >
    <span className={classes.switchThumb} />
  </button>
);

export const Kontakt = () => {
  const navigate = useNavigate();
  const { submitContactMessage, isSubmitting } = useSubmitContactMessage();
  const [succeeded, setSucceeded] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    listStudio: false,
    consent: false,
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name ist erforderlich';
    else if (formData.name.trim().length > CONTACT_FIELD_LIMITS.name) {
      newErrors.name = `Maximal ${CONTACT_FIELD_LIMITS.name} Zeichen`;
    }
    if (!formData.email.trim()) newErrors.email = 'E-Mail ist erforderlich';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Ungültige E-Mail-Adresse';
    else if (formData.email.trim().length > CONTACT_FIELD_LIMITS.email) {
      newErrors.email = 'E-Mail-Adresse ist zu lang';
    }
    if (!formData.message.trim()) newErrors.message = 'Nachricht ist erforderlich';
    else if (formData.message.trim().length > CONTACT_FIELD_LIMITS.message) {
      newErrors.message = `Maximal ${CONTACT_FIELD_LIMITS.message.toLocaleString('de-DE')} Zeichen`;
    }
    if (formData.phone.trim().length > CONTACT_FIELD_LIMITS.phone) {
      newErrors.phone = `Maximal ${CONTACT_FIELD_LIMITS.phone} Zeichen`;
    }
    if (!formData.consent) newErrors.consent = 'Zustimmung ist erforderlich';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    const result = await submitContactMessage(formData);

    if (result?.success) {
      setSucceeded(true);
      trackLead({
        leadType: 'contact_form',
        source: 'contact_page',
      });
    } else {
      setError(result?.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    }
  };

  const handleFertig = () => {
    navigate('/');
  };

  return (
    <div className={classes.page}>
      <nav className={classes.nav}>
        <div className={`${classes.container} ${classes.navContainer}`}>
          <Link to="/" aria-label="Stu Startseite">
            <svg width="63" height="22" viewBox="0 0 159 54" fill="none" aria-hidden="true">
              <path d="M150.22 26.8532C154.342 26.8532 157.683 23.5125 157.683 19.3915C157.683 15.2704 154.342 11.9297 150.22 11.9297C146.099 11.9297 142.758 15.2704 142.758 19.3915C142.758 23.5125 146.099 26.8532 150.22 26.8532Z" fill="#DF315A"/>
              <path d="M29.1399 27.7074L25.0967 27.3678C15.0923 26.5582 11.7229 25.5134 11.7229 23.2411C11.7229 20.9688 15.9735 19.7152 24.7339 19.7152C33.9867 19.7152 37.719 20.9427 38.4967 24.2335L38.6262 24.756H48.7861L48.6825 24.0246C47.6716 15.3534 39.948 11.1484 25.0967 11.1484C10.2455 11.1484 2.62546 15.2751 2.62546 23.3717C2.62546 30.7893 8.71631 34.3673 23.6972 35.7255L27.3256 36.0651C37.2526 36.9269 40.933 38.233 40.933 40.8448C40.933 43.6915 36.3972 45.1542 27.4296 45.1542C17.2694 45.1542 12.0857 43.2476 11.5933 39.3036L11.5155 38.7291H1.35547V39.4344C1.822 48.9152 10.3233 53.721 26.6519 53.721C42.9805 53.721 50.0562 49.2286 50.0562 40.3745C50.0562 32.4868 44.1986 28.9349 29.1399 27.7074Z" fill="white"/>
              <path d="M125.268 12.4583V29.3828C125.268 38.8636 119.073 44.7403 109.121 44.7403C99.7641 44.7403 96.1096 40.5353 96.1096 29.7224V12.4583H64.6963V0L55.3398 3.96994V39.6472C55.3398 49.4936 60.0569 53.6989 71.1242 53.6989C74.986 53.6989 78.6922 53.0458 81.8283 52.3928L82.3469 52.2884V43.9046L81.5693 44.0612C77.5002 44.8969 75.6858 45.2104 72.7829 45.2104C67.4178 45.2104 64.6963 43.9307 64.6963 36.9048V20.2937H91.3924L86.4678 24.0547V32.7781C86.4678 47.0648 92.481 53.6989 105.362 53.6989C113.682 53.6989 120.525 50.7997 125.268 45.2626V52.3928H134.91V12.4583H125.268Z" fill="white"/>
            </svg>
          </Link>
        </div>
      </nav>

      <div className={classes.content}>
        <div className={classes.container}>
          <h1 className={classes.pageTitle}>Kontakt</h1>

          <div className={classes.layout}>
            <div className={classes.infoCol}>
              <InfoCard label="E-Mail" value="info@stu-studios.de" href="mailto:info@stu-studios.de" icon={<MailIcon />} />
              <InfoCard label="Telefon" value="+49 176 20291843" href="tel:+4917620291843" icon={<PhoneIcon />} />
            </div>

            <section className={classes.formCard}>
              <h2 className={classes.formTitle}>Schreib uns gerne.</h2>

              {succeeded && (
                <div className={classes.successOverlay}>
                  <div className={classes.successMessage}>
                    <h2 className="titleSemibold">Vielen Dank für Ihre Nachricht!</h2>
                    <p className="regularMiddle">Wir werden uns so bald wie möglich bei Ihnen melden.</p>
                    <button type="button" onClick={handleFertig} className={classes.fertigButton}>
                      Zur Startseite
                    </button>
                  </div>
                </div>
              )}

              <form
                className={`${classes.form} ${succeeded ? classes.formDisabled : ''}`}
                onSubmit={onSubmit}
                noValidate
              >
                <div className={classes.fieldRow}>
                  <div className={`${classes.field} ${classes.fieldGrow}`}>
                    <label htmlFor="name" className={classes.fieldLabel}>Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      maxLength={CONTACT_FIELD_LIMITS.name}
                      autoComplete="name"
                      placeholder="Dein Name"
                      className={`${classes.fieldInput} ${errors.name ? classes.fieldInputError : ''}`}
                      required
                    />
                    {errors.name && <span className={classes.error}>{errors.name}</span>}
                  </div>

                  <div className={`${classes.field} ${classes.fieldGrow}`}>
                    <label htmlFor="email" className={classes.fieldLabel}>E-Mail Adresse</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      maxLength={CONTACT_FIELD_LIMITS.email}
                      autoComplete="email"
                      placeholder="du@beispiel.de"
                      className={`${classes.fieldInput} ${errors.email ? classes.fieldInputError : ''}`}
                      required
                    />
                    {errors.email && <span className={classes.error}>{errors.email}</span>}
                  </div>
                </div>

                <div className={classes.field}>
                  <label htmlFor="phone" className={classes.fieldLabel}>
                    Telefonnummer <span className={classes.optional}>(optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={CONTACT_FIELD_LIMITS.phone}
                    autoComplete="tel"
                    placeholder="+49..."
                    className={`${classes.fieldInput} ${errors.phone ? classes.fieldInputError : ''}`}
                  />
                  {errors.phone && <span className={classes.error}>{errors.phone}</span>}
                </div>

                <div className={classes.field}>
                  <label htmlFor="message" className={classes.fieldLabel}>Wie können wir dir helfen?</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={CONTACT_FIELD_LIMITS.message}
                    placeholder="Erzähl uns kurz von deinem Anliegen…"
                    className={`${classes.fieldInput} ${classes.textarea} ${errors.message ? classes.fieldInputError : ''}`}
                    required
                  />
                  {errors.message && <span className={classes.error}>{errors.message}</span>}
                </div>

                <div className={classes.studioToggle}>
                  <div className={classes.studioToggleText}>
                    <p className={classes.studioToggleTitle}>Mein Studio bei stu listen</p>
                    <p className={classes.studioToggleSubtitle}>
                      Ich habe Interesse mein Studio auf stu zu listen.
                    </p>
                  </div>
                  <StudioSwitch
                    checked={formData.listStudio}
                    onChange={(value) => setFormData((prev) => ({ ...prev, listStudio: value }))}
                  />
                </div>

                <div className={classes.consent}>
                  <input
                    type="checkbox"
                    id="consent"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    className={classes.consentInput}
                    required
                  />
                  <label htmlFor="consent" className={classes.consentLabel}>
                    Ich bin damit einverstanden, dass diese Website meine personenbezogenen Daten speichert und verarbeitet.
                  </label>
                </div>
                {errors.consent && <span className={classes.error}>{errors.consent}</span>}

                <div className={classes.submitRow}>
                  <button type="submit" className={classes.submitButton} disabled={isSubmitting || succeeded}>
                    {isSubmitting ? 'Wird gesendet...' : succeeded ? 'Gesendet' : 'Senden'}
                  </button>
                </div>

                {error && <p className={classes.errorMessage} role="alert">{error}</p>}
              </form>
            </section>
          </div>
        </div>
      </div>

      <BottomFooter fromKontakt />
    </div>
  );
};

export default Kontakt;
